import { Helmet } from "react-helmet-async";

/**
 * SEOHead — per-route Open Graph / Twitter / SEO meta tags.
 *
 * Renders into <head> via react-helmet-async so the tags update on client
 * navigation. Facebook/LinkedIn crawlers don't run JS, so for maximum
 * social preview reliability the same tags are ALSO baked into the SEO
 * landing pages' initial payloads via SSR/pre-render at a future date.
 * Twitter, WhatsApp, Slack, Discord all render the JS-injected tags fine.
 *
 * Props:
 *   title:       page <title>
 *   description: 155-char meta description
 *   image:       absolute URL to a 1200x630 PNG (from /og/... in public/)
 *   path:        the canonical path (e.g. /us-stem-opt-degrees)
 *   siteName:    default "WhichUniversityAdvisor.com"
 *   type:        og:type — default "website"
 */
export default function SEOHead({
  title,
  description,
  image,
  path = "",
  siteName = "WhichUniversityAdvisor.com",
  type = "website",
}) {
  // Prefer the deployed origin so social crawlers hit the public URL; fall
  // back to the current origin (works in preview / dev).
  const origin =
    (typeof window !== "undefined" && window.__WUA_CANONICAL_ORIGIN__) ||
    (typeof window !== "undefined" && window.location.origin) ||
    "https://whichuniversityadvisor.com";
  const url = `${origin}${path}`;
  const imgUrl = image?.startsWith("http") ? image : `${origin}${image}`;

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={url} />

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={imgUrl} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={title} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={imgUrl} />
    </Helmet>
  );
}
